import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { 
  Calculator, 
  Info,
  AlertTriangle,
  Users,
  Baby,
  Wallet,
  Building2,
  ChevronDown,
  ChevronUp,
  Euro,
  Banknote,
  GraduationCap,
  Heart
} from 'lucide-react';

// =============================================
// CONSTANTE LEGISLAȚIE ROMÂNIA 2026
// =============================================

// Salariul minim brut 2026
const SALARIU_MINIM = {
  IANUARIE_IUNIE: 4050,    // 1 ianuarie - 30 iunie 2026
  IULIE_DECEMBRIE: 4325    // 1 iulie - 31 decembrie 2026
};

// Contribuții angajat (reținute din brut)
const CONTRIBUTII_ANGAJAT = {
  CAS: 0.25,       // 25% - Contribuția pentru pensii
  CASS: 0.10       // 10% - Contribuția pentru sănătate
};

// Impozit pe venit
const IMPOZIT_VENIT = 0.10; // 10%

// Contribuții angajator (cost suplimentar)
const CONTRIBUTII_ANGAJATOR = {
  CAM: 0.0225     // 2.25% - Contribuția asiguratorie pentru muncă
};

// Plafon deducere personală (salariul minim + 2000 lei)
const PLAFON_DEDUCERE = 2000; // lei peste salariul minim

// Sumă neimpozabilă din salariul minim
const SUMA_NEIMPOZABILA = {
  IANUARIE_IUNIE: 300,    // Până la 30 iunie 2026
  IULIE_DECEMBRIE: 200    // De la 1 iulie 2026
};

// Deducere suplimentară pentru copii școlari
const DEDUCERE_COPIL_SCOLAR = 100; // lei/copil/lună

// NOTĂ: Deducerea specială pentru tineri sub 26 ani a fost ELIMINATĂ din ianuarie 2025
// Tinerii plătesc aceleași taxe ca restul salariaților

// Tabel deduceri personale de bază (procent din salariul minim)
// Valorile cresc în funcție de numărul de persoane în întreținere
const DEDUCERE_PERSONALA_BAZA = {
  0: 0.20,   // 20% din salariul minim - fără persoane în întreținere
  1: 0.25,   // 25% din salariul minim - 1 persoană
  2: 0.30,   // 30% din salariul minim - 2 persoane
  3: 0.35,   // 35% din salariul minim - 3 persoane
  4: 0.45    // 45% din salariul minim - 4+ persoane
};

// =============================================
// FUNCȚII DE CALCUL
// =============================================

// Obține salariul minim în funcție de perioadă
const getSalariuMinim = (perioada) => {
  return perioada === 'ianuarie_iunie' ? SALARIU_MINIM.IANUARIE_IUNIE : SALARIU_MINIM.IULIE_DECEMBRIE;
};

// Obține suma neimpozabilă în funcție de perioadă
const getSumaNeimpozabila = (perioada) => {
  return perioada === 'ianuarie_iunie' ? SUMA_NEIMPOZABILA.IANUARIE_IUNIE : SUMA_NEIMPOZABILA.IULIE_DECEMBRIE;
};

// Calculează deducerea personală de bază
const calculeazaDeducerePersonala = (salariuBrut, nrPersoaneIntretinere, salariuMinim) => {
  // Plafon pentru deducere: salariul minim + 2000 lei
  const plafon = salariuMinim + PLAFON_DEDUCERE;
  
  // Dacă brutul depășește plafonul, nu se acordă deducere
  if (salariuBrut > plafon) return 0;
  
  // Determină procentul în funcție de persoane în întreținere
  const nrPersoane = Math.min(nrPersoaneIntretinere, 4);
  const procent = DEDUCERE_PERSONALA_BAZA[nrPersoane];
  
  // Deducerea maximă (la salariul minim)
  const deducereMaxima = salariuMinim * procent;
  
  // Calculăm factorul de reducere (scade liniar de la minim la plafon)
  if (salariuBrut <= salariuMinim) {
    return Math.round(deducereMaxima);
  }
  
  // Scădere liniară între salariul minim și plafon
  const factorReducere = 1 - ((salariuBrut - salariuMinim) / PLAFON_DEDUCERE);
  return Math.round(Math.max(0, deducereMaxima * factorReducere));
};

// Calculează deducerea pentru copii școlari
const calculeazaDeducereCopiiScolari = (nrCopiiScolari) => {
  return nrCopiiScolari * DEDUCERE_COPIL_SCOLAR;
};

// =============================================
// COMPONENTA PRINCIPALĂ
// =============================================

const SalaryCalculator = () => {
  // State-uri
  const [salariuBrut, setSalariuBrut] = useState('');
  const [perioada, setPerioada] = useState('iulie_decembrie'); // implicit semestrul 2
  const [nrPersoaneIntretinere, setNrPersoaneIntretinere] = useState(0);
  const [nrCopiiScolari, setNrCopiiScolari] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  
  // Setări avansate (editabile)
  const [sumaNeimpozabilaCustom, setSumaNeimpozabilaCustom] = useState('');
  const [useCustomNeimpozabil, setUseCustomNeimpozabil] = useState(false);
  
  // Rezultate
  const [rezultat, setRezultat] = useState(null);

  // Calculează automat la fiecare modificare
  useEffect(() => {
    calculeazaSalariu();
  }, [salariuBrut, perioada, nrPersoaneIntretinere, nrCopiiScolari, sumaNeimpozabilaCustom, useCustomNeimpozabil]);

  const calculeazaSalariu = () => {
    const brut = parseFloat(salariuBrut) || 0;
    if (brut <= 0) {
      setRezultat(null);
      return;
    }

    const salariuMinim = getSalariuMinim(perioada);
    const sumaNeimpozabilaStandard = getSumaNeimpozabila(perioada);
    
    // Suma neimpozabilă (poate fi custom sau standard)
    const sumaNeimpozabila = useCustomNeimpozabil && sumaNeimpozabilaCustom !== '' 
      ? parseFloat(sumaNeimpozabilaCustom) || 0 
      : sumaNeimpozabilaStandard;

    // =============================================
    // CALCUL CONTRIBUȚII ANGAJAT
    // =============================================
    
    // Baza pentru contribuții (brut - sumă neimpozabilă, dar doar dacă e salariul minim)
    let bazaContributii = brut;
    let scutireAplicata = 0;
    
    // Scutirea se aplică doar dacă salariul e egal cu minimul sau aproape
    if (brut <= salariuMinim * 1.1 && sumaNeimpozabila > 0) {
      scutireAplicata = Math.min(sumaNeimpozabila, brut);
      bazaContributii = brut - scutireAplicata;
    }
    
    // CAS (25%)
    const cas = Math.round(bazaContributii * CONTRIBUTII_ANGAJAT.CAS);
    
    // CASS (10%)
    const cass = Math.round(bazaContributii * CONTRIBUTII_ANGAJAT.CASS);
    
    // =============================================
    // CALCUL DEDUCERI PERSONALE
    // =============================================
    
    // Deducere personală de bază
    const deducerePersonalaBaza = calculeazaDeducerePersonala(brut, nrPersoaneIntretinere, salariuMinim);
    
    // Deducere copii școlari (100 lei/copil)
    const deducereCopiiScolari = calculeazaDeducereCopiiScolari(nrCopiiScolari);
    
    // Total deduceri (fără deducere tineri - eliminată din 2025)
    const totalDeduceri = deducerePersonalaBaza + deducereCopiiScolari;
    
    // =============================================
    // CALCUL VENIT IMPOZABIL ȘI IMPOZIT
    // =============================================
    
    // Venit impozabil = Brut - CAS - CASS - Deduceri (minim 0)
    const venitImpozabil = Math.max(0, brut - cas - cass - totalDeduceri);
    
    // Impozit pe venit (10%)
    const impozit = Math.round(venitImpozabil * IMPOZIT_VENIT);
    
    // =============================================
    // CALCUL SALARIU NET
    // =============================================
    
    const salariuNet = brut - cas - cass - impozit;
    
    // =============================================
    // CALCUL COSTURI ANGAJATOR
    // =============================================
    
    // CAM (2.25%)
    const cam = Math.round(brut * CONTRIBUTII_ANGAJATOR.CAM);
    const costTotalAngajator = brut + cam;
    
    // =============================================
    // SALVEAZĂ REZULTATUL
    // =============================================
    
    setRezultat({
      salariuBrut: brut,
      salariuMinim,
      sumaNeimpozabila,
      scutireAplicata,
      bazaContributii,
      // Contribuții angajat
      cas,
      cass,
      totalContributiiAngajat: cas + cass,
      // Deduceri
      deducerePersonalaBaza,
      deducereCopiiScolari,
      totalDeduceri,
      // Impozit
      venitImpozabil,
      impozit,
      // Net
      salariuNet,
      // Angajator
      cam,
      costTotalAngajator,
      // Procente efective
      procentRetineri: ((brut - salariuNet) / brut * 100).toFixed(1),
      procentNet: (salariuNet / brut * 100).toFixed(1)
    });
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('ro-RO', { 
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    }).format(Math.round(value)) + ' lei';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calculator className="w-6 h-6 text-green-600" />
            Calculator Salariu România 2026
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Conform Codului Fiscal 2026 • CAS 25% • CASS 10% • Impozit 10%
          </p>
        </div>
      </div>

      {/* Formular */}
      <Card className="border-2 border-green-200">
        <CardContent className="p-6 space-y-6">
          
          {/* Salariul brut */}
          <div className="space-y-2">
            <Label className="text-base font-medium flex items-center gap-2">
              <Wallet className="w-4 h-4 text-green-600" />
              Salariul brut lunar (lei)
            </Label>
            <Input
              type="number"
              value={salariuBrut}
              onChange={(e) => setSalariuBrut(e.target.value)}
              placeholder="Ex: 5000"
              className="text-lg font-semibold"
            />
            <div className="flex gap-2 flex-wrap">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSalariuBrut(getSalariuMinim(perioada).toString())}
              >
                Salariu minim ({formatCurrency(getSalariuMinim(perioada))})
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSalariuBrut('5000')}
              >
                5.000 lei
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSalariuBrut('7500')}
              >
                7.500 lei
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setSalariuBrut('10000')}
              >
                10.000 lei
              </Button>
            </div>
          </div>

          {/* Perioada */}
          <div className="space-y-2">
            <Label className="text-base font-medium">Perioada</Label>
            <div className="flex gap-3">
              <Button
                variant={perioada === 'ianuarie_iunie' ? 'default' : 'outline'}
                onClick={() => setPerioada('ianuarie_iunie')}
                className={perioada === 'ianuarie_iunie' ? 'bg-green-500 hover:bg-green-600' : ''}
              >
                Ian - Iun 2026
              </Button>
              <Button
                variant={perioada === 'iulie_decembrie' ? 'default' : 'outline'}
                onClick={() => setPerioada('iulie_decembrie')}
                className={perioada === 'iulie_decembrie' ? 'bg-green-500 hover:bg-green-600' : ''}
              >
                Iul - Dec 2026
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Salariul minim: {perioada === 'ianuarie_iunie' ? '4.050 lei' : '4.325 lei'} • 
              Sumă neimpozabilă: {perioada === 'ianuarie_iunie' ? '300 lei' : '200 lei'}
            </p>
          </div>

          {/* Persoane în întreținere */}
          <div className="space-y-2">
            <Label className="text-base font-medium flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" />
              Persoane în întreținere
            </Label>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setNrPersoaneIntretinere(Math.max(0, nrPersoaneIntretinere - 1))}
                className="w-10 h-10"
              >
                -
              </Button>
              <span className="text-xl font-bold w-10 text-center">{nrPersoaneIntretinere}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setNrPersoaneIntretinere(nrPersoaneIntretinere + 1)}
                className="w-10 h-10"
              >
                +
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Include: soț/soție, copii, alte rude cu venituri mici (sub 510 lei/lună)
            </p>
          </div>

          {/* Copii școlari */}
          <div className="space-y-2">
            <Label className="text-base font-medium flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-purple-600" />
              Copii școlari (până la 18 ani)
            </Label>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setNrCopiiScolari(Math.max(0, nrCopiiScolari - 1))}
                className="w-10 h-10"
              >
                -
              </Button>
              <span className="text-xl font-bold w-10 text-center">{nrCopiiScolari}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setNrCopiiScolari(nrCopiiScolari + 1)}
                className="w-10 h-10"
              >
                +
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Deducere suplimentară: {DEDUCERE_COPIL_SCOLAR} lei/copil/lună (fără plafon de venit)
            </p>
          </div>

          {/* Setări avansate */}
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <Label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-3">
              <Info className="w-4 h-4" />
              Setări avansate (opțional)
            </Label>
            
            <div className="flex items-start gap-3">
              <input 
                type="checkbox" 
                id="customNeimpozabil"
                checked={useCustomNeimpozabil}
                onChange={(e) => setUseCustomNeimpozabil(e.target.checked)}
                className="w-4 h-4 mt-1"
              />
              <div className="flex-1">
                <Label htmlFor="customNeimpozabil" className="text-sm cursor-pointer">
                  Sumă neimpozabilă personalizată
                </Label>
                {useCustomNeimpozabil && (
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      type="number"
                      value={sumaNeimpozabilaCustom}
                      onChange={(e) => setSumaNeimpozabilaCustom(e.target.value)}
                      placeholder={getSumaNeimpozabila(perioada).toString()}
                      className="w-32"
                    />
                    <span className="text-sm text-gray-600">lei</span>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Implicit: {getSumaNeimpozabila(perioada)} lei (scutită de CAS, CASS, impozit pentru salariul minim)
                </p>
              </div>
            </div>
          </div>

          {/* =============================================
              REZULTATE
              ============================================= */}
          {rezultat && (
            <>
              <hr className="border-green-300" />
              
              {/* SALARIU NET - Mare și vizibil */}
              <div className="p-6 bg-gradient-to-r from-green-500 to-green-600 rounded-lg text-white text-center">
                <p className="text-sm opacity-80 mb-1">SALARIUL NET</p>
                <p className="text-4xl font-bold">{formatCurrency(rezultat.salariuNet)}</p>
                <p className="text-sm opacity-80 mt-2">
                  {rezultat.procentNet}% din brut • Rețineri: {rezultat.procentRetineri}%
                </p>
              </div>

              {/* Contribuții angajat */}
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <h3 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                  <Banknote className="w-5 h-5" />
                  Contribuții Angajat (reținute din brut)
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center py-1 border-b border-red-200">
                    <div>
                      <span>CAS - Contribuție pensii</span>
                      <span className="text-xs text-red-600 ml-1">(25%)</span>
                    </div>
                    <span className="font-medium text-red-700">{formatCurrency(rezultat.cas)}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-red-200">
                    <div>
                      <span>CASS - Contribuție sănătate</span>
                      <span className="text-xs text-red-600 ml-1">(10%)</span>
                    </div>
                    <span className="font-medium text-red-700">{formatCurrency(rezultat.cass)}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-red-200">
                    <div>
                      <span>Impozit pe venit</span>
                      <span className="text-xs text-red-600 ml-1">(10%)</span>
                    </div>
                    <span className="font-medium text-red-700">{formatCurrency(rezultat.impozit)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 bg-red-100 px-2 rounded font-bold">
                    <span>Total rețineri</span>
                    <span className="text-red-800">{formatCurrency(rezultat.cas + rezultat.cass + rezultat.impozit)}</span>
                  </div>
                </div>
              </div>

              {/* Deduceri personale */}
              {rezultat.totalDeduceri > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Deduceri Personale (reduc impozitul)
                  </h3>
                  <div className="space-y-2">
                    {rezultat.deducerePersonalaBaza > 0 && (
                      <div className="flex justify-between items-center py-1 border-b border-blue-200">
                        <span>Deducere personală de bază ({nrPersoaneIntretinere} pers.)</span>
                        <span className="font-medium text-blue-700">{formatCurrency(rezultat.deducerePersonalaBaza)}</span>
                      </div>
                    )}
                    {rezultat.deducereTineri > 0 && (
                      <div className="flex justify-between items-center py-1 border-b border-blue-200">
                        <span>Deducere tineri sub 26 ani (15%)</span>
                        <span className="font-medium text-blue-700">{formatCurrency(rezultat.deducereTineri)}</span>
                      </div>
                    )}
                    {rezultat.deducereCopiiScolari > 0 && (
                      <div className="flex justify-between items-center py-1 border-b border-blue-200">
                        <span>Deducere copii școlari ({nrCopiiScolari} × 100 lei)</span>
                        <span className="font-medium text-blue-700">{formatCurrency(rezultat.deducereCopiiScolari)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center py-2 bg-blue-100 px-2 rounded font-bold">
                      <span>Total deduceri</span>
                      <span className="text-blue-800">{formatCurrency(rezultat.totalDeduceri)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Cost angajator */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h3 className="font-semibold text-amber-700 mb-3 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Cost Total Angajator
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center py-1 border-b border-amber-200">
                    <span>Salariul brut</span>
                    <span className="font-medium">{formatCurrency(rezultat.salariuBrut)}</span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-amber-200">
                    <div>
                      <span>CAM - Contribuție asiguratorie muncă</span>
                      <span className="text-xs text-amber-600 ml-1">(2.25%)</span>
                    </div>
                    <span className="font-medium text-amber-700">{formatCurrency(rezultat.cam)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 bg-amber-100 px-2 rounded font-bold">
                    <span>Cost total angajator</span>
                    <span className="text-amber-800">{formatCurrency(rezultat.costTotalAngajator)}</span>
                  </div>
                </div>
              </div>

              {/* Toggle detalii */}
              <Button 
                variant="ghost" 
                onClick={() => setShowDetails(!showDetails)}
                className="w-full text-blue-600"
              >
                {showDetails ? 'Ascunde detalii calcul' : 'Vezi detalii calcul'}
                {showDetails ? <ChevronUp className="ml-2 w-4 h-4" /> : <ChevronDown className="ml-2 w-4 h-4" />}
              </Button>

              {showDetails && (
                <div className="bg-gray-50 p-4 rounded-lg space-y-3 text-sm">
                  <p className="font-semibold text-gray-700 border-b pb-2">📊 Detalii calcul:</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="font-medium text-gray-600">Parametri:</p>
                      <ul className="text-xs space-y-1 mt-1">
                        <li>• Salariu minim: {formatCurrency(rezultat.salariuMinim)}</li>
                        <li>• Plafon deduceri: {formatCurrency(rezultat.salariuMinim + PLAFON_DEDUCERE)}</li>
                        <li>• Sumă neimpozabilă: {formatCurrency(rezultat.sumaNeimpozabila)}</li>
                        {rezultat.scutireAplicata > 0 && (
                          <li>• Scutire aplicată: {formatCurrency(rezultat.scutireAplicata)}</li>
                        )}
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium text-gray-600">Formula calcul net:</p>
                      <ul className="text-xs space-y-1 mt-1">
                        <li>• Brut: {formatCurrency(rezultat.salariuBrut)}</li>
                        <li>• - CAS (25%): {formatCurrency(rezultat.cas)}</li>
                        <li>• - CASS (10%): {formatCurrency(rezultat.cass)}</li>
                        <li>• - Deduceri: {formatCurrency(rezultat.totalDeduceri)}</li>
                        <li>• = Venit impozabil: {formatCurrency(rezultat.venitImpozabil)}</li>
                        <li>• - Impozit (10%): {formatCurrency(rezultat.impozit)}</li>
                        <li className="font-bold">• = NET: {formatCurrency(rezultat.salariuNet)}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Notă legislativă */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <p className="font-semibold mb-1">⚠️ Notă importantă:</p>
          <p>
            Calculele sunt orientative, bazate pe Codul Fiscal 2026. 
            Deducerea personală scade progresiv pentru venituri peste salariul minim + 2.000 lei.
            Suma neimpozabilă (300/200 lei) se aplică doar pentru salarii apropiate de minim.
            Pentru situații specifice, consultați un expert contabil.
          </p>
        </div>
      </div>

      {/* Info legislație */}
      <div className="text-xs text-gray-400 text-center">
        Calculator actualizat conform: Codul Fiscal 2026, OUG salariul minim, Norme ANAF deduceri personale
      </div>
    </div>
  );
};

export default SalaryCalculator;
